import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from 'redux/store';
import type { CountryCode } from 'types/countries';
import {
    enabledCountryNames,
    hasCanada,
    hasCountry,
    hasSelfServeMarket,
    normalizeEnabledCountryCodes,
} from 'utils/countries';

export interface EnabledCountries {
    codes: CountryCode[];
    names: string[];
    has: (code: CountryCode) => boolean;
    hasCanada: boolean;
    hasSelfServeMarket: boolean;
}

export function useEnabledCountries(): EnabledCountries {
    const rawCodes = useSelector((state: RootState) => state.user.entity.enabled_country_codes);
    const codes = useMemo(() => normalizeEnabledCountryCodes(rawCodes), [rawCodes]);
    const names = useMemo(() => enabledCountryNames(codes), [codes]);
    const has = useCallback((code: CountryCode) => hasCountry(codes, code), [codes]);
    const canada = hasCanada(codes);
    const selfServe = hasSelfServeMarket(codes);

    return useMemo(
        () => ({
            codes,
            names,
            has,
            hasCanada: canada,
            hasSelfServeMarket: selfServe,
        }),
        [canada, codes, has, names, selfServe]
    );
}

export default useEnabledCountries;
